import * as util from "lib/util"
import { UseFormReturn } from "react-hook-form"
import * as opt from "lib/opt"
import * as valid from "lib/valid"
import * as fmt from "lib/fmt"

export * from "./types"
export * from "./containmentFormData"

/**
 * Creates a form validator function based on a react-hook-form instance
 *
 * @param f - The react-hook-form instance
 * @param options - Configuration options for the validator
 * @returns A validator function that checks if specified form fields are valid
 */
export function GetFormValidator<T extends Record<string, any>>(
	f: UseFormReturn<T, any, undefined>
) {
	/**
	 * Validates if the form has valid values for the specified keys
	 * If no keys are provided, validates all keys in the form values
	 *
	 * @param keysToCheck - Array of keys to validate. If empty, all keys will be validated.
	 * @returns Boolean indicating if all specified fields are valid
	 */
	return (keysToCheck?: Array<keyof T>): bool => {
		const values = f.getValues()

		// If no keys provided, use all keys from the form values
		const keysToValidate =
			keysToCheck && !keysToCheck.IsEmpty() ? keysToCheck : (values.Keys() as Array<keyof T>)

		// Continue with validation for valid keys
		return keysToValidate.All((key) => {
			// Check if there are no errors for the key in formState
			if (f.formState.errors[key]) {
				return false;
			}
			return !IsFormEmpty(values, key);
		})
	}
}

export function IsFormEmpty<T extends Record<string, any>>(values: T, key: keyof T): boolean {
	return values
		.Get<T, keyof T>(key)
		.IfSome((v: any) => {
			if (opt.IsOpt(v)) {
				return !v.IsSome(); // Return false if the value exists
			}
			if (v instanceof File) {
				return false; // Return false if the value is a File
			}
			if (Array.isArray(v) || util.IsPlainObject(v)) {
				return Object.values(v).IsEmpty(); // Return true if the array or object is empty
			}
			if (valid.IsString(v)) {
				return v.IsEmpty(); // Return true if the string is empty
			}
			return isNil(v); // Return true if the value is null or undefined
		})
		.Expect(fmt.Sprintf("No value found for key: %s", String(key)));
}

